'use client';

import { useState } from 'react';
import Image from 'next/image';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, BookOpen } from 'lucide-react';

// Type definitions for book pages
type CoverPage = { type: 'cover'; image: string };
type ArtworkPage = { type: 'artwork'; image: string; label: string };
type StoryPage = { type: 'story'; text: string; illustration: string };
type EndPage = { type: 'end'; message: string };
type BookPage = CoverPage | ArtworkPage | StoryPage | EndPage;

// Sample book data - generated from Miya's bunny artwork
const sampleBook = {
  title: "Miya's Bunny Friends",
  childName: "Miya",
  childAge: 3,
  content: `Miya played in a sunny garden. The bright sun shone high. Green grass wiggled gently. Colorful flowers bloomed everywhere. A red butterfly flew by. A big white bunny hopped! A tiny baby bunny sniffed a yellow flower. "Look!" said Miya.

But the little baby bunny felt hungry. Its tiny tummy gave a big rumble. Oh dear! The baby bunny looked worried. Its soft fur bristled a bit. The big bunny gave a gentle nudge. What could Miya do to help?

Miya looked down at the soft grass. She saw two yummy orange carrots! "Here you go!" Miya whispered. The bunnies hopped over quickly. They went munch! munch! munch! The yummy carrots filled their tummies. Now everyone felt happy and very sleepy.`,
};

// Split content into paragraphs for pages
const paragraphs = sampleBook.content.split('\n\n');

// Book pages configuration (6 pages: cover, artwork, 3 story pages, end)
const pages: BookPage[] = [
  { type: 'cover', image: '/marketing/sample-book/cover.png?v=3' },
  { type: 'artwork', image: '/marketing/sample-book/artwork.jpeg', label: "Miya's Original Artwork" },
  { type: 'story', text: paragraphs[0], illustration: '/marketing/sample-book/illustration-1.png?v=3' },
  { type: 'story', text: paragraphs[1], illustration: '/marketing/sample-book/illustration-2.png?v=3' },
  { type: 'story', text: paragraphs[2], illustration: '/marketing/sample-book/illustration-3.png?v=3' },
  { type: 'end', message: 'The End' },
];

/**
 * Interactive Book Preview component
 * Shows a flipbook-style preview of a sample generated story
 */
export function BookPreview() {
  const [currentPage, setCurrentPage] = useState(0);
  const [direction, setDirection] = useState(0);

  const goToPage = (newPage: number) => {
    if (newPage < 0 || newPage >= pages.length) return;
    setDirection(newPage > currentPage ? 1 : -1);
    setCurrentPage(newPage);
  };

  const nextPage = () => goToPage(currentPage + 1);
  const prevPage = () => goToPage(currentPage - 1);

  const page = pages[currentPage];

  const pageVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0,
      rotateY: direction > 0 ? -15 : 15,
    }),
    center: {
      x: 0,
      opacity: 1,
      rotateY: 0,
    },
    exit: (direction: number) => ({
      x: direction > 0 ? -300 : 300,
      opacity: 0,
      rotateY: direction > 0 ? 15 : -15,
    }),
  };

  return (
    <section id="book-preview" className="py-16 md:py-24 bg-cream-100">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-6xl">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center gap-2 mb-4">
            <BookOpen className="w-6 h-6 text-terracotta-500" />
            <span className="text-terracotta-500 font-medium text-sm uppercase tracking-wide">
              See It In Action
            </span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-semibold text-charcoal-600 mb-4">
            From Artwork to Storybook
          </h2>
          <p className="text-taupe-500 text-base md:text-lg max-w-2xl mx-auto">
            Watch how Miya&apos;s bunny drawing became a magical bedtime adventure
          </p>
        </motion.div>

        {/* Book Container */}
        <div className="flex flex-col items-center">
          {/* Book */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="relative w-full max-w-2xl"
          >
            {/* Book Shadow */}
            <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-[90%] h-8 bg-charcoal-600/10 blur-xl rounded-full" />

            {/* Book Frame */}
            <div className="relative bg-white rounded-lg shadow-xl overflow-hidden aspect-[4/3] md:aspect-[3/2]">
              {/* Page Content */}
              <AnimatePresence mode="wait" custom={direction}>
                <motion.div
                  key={currentPage}
                  custom={direction}
                  variants={pageVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{
                    x: { type: 'spring', stiffness: 300, damping: 30 },
                    opacity: { duration: 0.2 },
                    rotateY: { duration: 0.3 },
                  }}
                  className="absolute inset-0 bg-cream-50"
                >
                  {/* Cover Page */}
                  {page.type === 'cover' && (
                    <div className="h-full flex flex-col items-center justify-center p-6 bg-gradient-to-b from-sage-100 to-cream-100">
                      <div className="relative w-full max-w-[280px] aspect-square mb-4">
                        <Image
                          src={page.image}
                          alt="Book cover"
                          fill
                          className="object-contain rounded-lg shadow-lg"
                        />
                      </div>
                      <h3 className="text-xl md:text-2xl font-serif font-semibold text-charcoal-600 text-center">
                        {sampleBook.title}
                      </h3>
                      <p className="text-sm text-taupe-500 mt-2">
                        A story for {sampleBook.childName}, age {sampleBook.childAge}
                      </p>
                    </div>
                  )}

                  {/* Original Artwork Page */}
                  {page.type === 'artwork' && (
                    <div className="h-full flex flex-col items-center justify-center p-6">
                      <div className="relative w-full max-w-[320px] aspect-square mb-4">
                        <Image
                          src={page.image}
                          alt="Original artwork"
                          fill
                          className="object-contain rounded-lg shadow-md"
                        />
                      </div>
                      <p className="text-sm font-medium text-terracotta-500 bg-terracotta-50 px-4 py-2 rounded-full">
                        {page.label}
                      </p>
                    </div>
                  )}

                  {/* Story Pages */}
                  {page.type === 'story' && (
                    <div className="h-full flex flex-col md:flex-row p-4 md:p-8 gap-4 md:gap-6">
                      {page.illustration && (
                        <div className="flex-shrink-0 flex items-center justify-center md:w-1/2">
                          <div className="relative w-full max-w-[280px] md:max-w-[320px] aspect-square">
                            <Image
                              src={page.illustration}
                              alt="Story illustration"
                              fill
                              className="object-contain"
                            />
                          </div>
                        </div>
                      )}
                      <div className={`flex-1 flex items-center ${page.illustration ? 'md:w-1/2' : ''}`}>
                        <p className="text-base md:text-lg lg:text-xl text-charcoal-600 leading-relaxed font-serif">
                          {page.text}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* End Page */}
                  {page.type === 'end' && (
                    <div className="h-full flex flex-col items-center justify-center p-6 bg-gradient-to-b from-cream-100 to-sage-50">
                      <div className="text-4xl md:text-5xl font-serif font-semibold text-charcoal-600 mb-4">
                        {page.message}
                      </div>
                      <div className="w-16 h-1 bg-terracotta-400 rounded-full mb-4" />
                      <p className="text-sm text-taupe-500 text-center">
                        Every artwork tells a story.<br />
                        Let us help you tell yours.
                      </p>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>

              {/* Page Number */}
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 text-xs text-taupe-400">
                {currentPage + 1} / {pages.length}
              </div>
            </div>

            {/* Navigation Buttons */}
            <button
              onClick={prevPage}
              disabled={currentPage === 0}
              className="absolute left-0 md:-left-16 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 bg-white rounded-full shadow-lg flex items-center justify-center text-charcoal-600 hover:bg-cream-100 disabled:opacity-30 disabled:cursor-not-allowed transition-all z-10"
              aria-label="Previous page"
            >
              <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
            </button>
            <button
              onClick={nextPage}
              disabled={currentPage === pages.length - 1}
              className="absolute right-0 md:-right-16 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 bg-white rounded-full shadow-lg flex items-center justify-center text-charcoal-600 hover:bg-cream-100 disabled:opacity-30 disabled:cursor-not-allowed transition-all z-10"
              aria-label="Next page"
            >
              <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
            </button>
          </motion.div>

          {/* Page Dots */}
          <div className="flex gap-2 mt-6">
            {pages.map((_, index) => (
              <button
                key={index}
                onClick={() => goToPage(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentPage
                    ? 'bg-terracotta-500 w-6'
                    : 'bg-taupe-300 hover:bg-taupe-400'
                }`}
                aria-label={`Go to page ${index + 1}`}
              />
            ))}
          </div>

          {/* Caption */}
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="text-center text-sm text-taupe-400 mt-6 max-w-md"
          >
            This story was generated from a child&apos;s bunny drawing using our AI.
            Perfect for bedtime reading with your little ones.
          </motion.p>
        </div>
      </div>
    </section>
  );
}
